import { CSSProperties, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import { getBillingPortalUrl_api } from 'api/paymentsV2Api';
import { goToUrl } from 'modules/Utils';
import { SHARED_TOAST_OPTIONS_ERROR } from 'modules/Toast';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import useUser from 'Hooks/useUser';

interface OrganizationPaymentPastDueModalProps {
  /** Whether to show the modal automatically when past due is detected */
  autoShow?: boolean;
  /** Manual control over modal visibility - overrides autoShow when provided */
  isOpen?: boolean;
  /** Callback when modal is closed */
  onClose?: () => void;
}

const OrganizationPaymentPastDueModal = ({
  autoShow = true,
  isOpen,
  onClose
}: OrganizationPaymentPastDueModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);

  const { organization, getIsOrganizationPaymentPastDue } = useOrganizationV2();
  const { isSymphonyAdmin } = useUser();

  // Auto-show logic: only show if autoShow is true, user is admin, and we haven't shown this session
  useEffect(() => {
    if (isOpen !== undefined) {
      setShowModal(isOpen);
      return;
    }

    if (autoShow && getIsOrganizationPaymentPastDue() && !hasShownThisSession) {
      setShowModal(true);
      setHasShownThisSession(true);
    }
  }, [autoShow, hasShownThisSession, isOpen]);

  const handleUpdateBilling = async () => {
    if (!organization?.id) {
      toast.error('Unable to access billing details. Please try again.', SHARED_TOAST_OPTIONS_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      const url = await getBillingPortalUrl_api({ organizationId: organization.id });
      goToUrl(url, setIsLoading);
      handleCloseModal(); // Close modal after redirecting
    } catch (error) {
      setIsLoading(false);
      Sentry.captureException(error);
      console.error('Error accessing billing portal:', error);
      toast.error('Something went wrong. Please try again or contact support.', SHARED_TOAST_OPTIONS_ERROR);
    }
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    setShowModal(false);
  };

  const handleContactSupport = () => {
    window.open('mailto:support@symphony.is?subject=Payment Issue - Organization Subscription', '_blank');
    handleCloseModal();
  };

  if (!showModal || !organization) {
    return null;
  }

  const sty: Record<string, CSSProperties> = {
    content: {
      width: '100%',
      textAlign: 'center'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      justifyContent: 'center',
      flexDirection: 'column'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1F2937',
      margin: '0 0 8px 0',
      fontFamily: 'DIN, sans-serif',
    },
    body: {
      marginBottom: '32px',
    },
    description: {
      fontSize: '16px',
      lineHeight: '1.5',
      color: '#4B5563',
      margin: '0 0 16px 0',
      fontFamily: 'DIN, sans-serif',
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'center',
    },
    primaryButton: {
      width: '100%',
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '600',
    },
    secondaryButton: {
      fontSize: '14px',
      color: '#6B7280',
    },
  };

  return (
    <ModalSymphony
      size="medium"
      className="organizationPaymentPastDueModal"
      isOpen={showModal}
      onClose={handleCloseModal}
      isHideCloseButton={!isSymphonyAdmin}
    >
      <div className="organizationPaymentPastDueModalContent" style={sty.content}>
        <div className="organizationPaymentPastDueModalHeader" style={sty.header}>
          <Icon icon="warning" size={36} color={Colors.red} className="organizationPaymentPastDueModalIcon" />
          <h2 style={sty.title}>Your team subscription is past due.</h2>
        </div>

        <div className="organizationPaymentPastDueModalBody" style={sty.body}>
          <p style={sty.description}>
            Your team subscription payment is past due. Please update your payment method to continue using Symphony's team features and avoid service interruption.
          </p>
        </div>

        <div className="organizationPaymentPastDueModalActions" style={sty.actions}>
          <ButtonSymphony
            className="organizationPaymentPastDueModalUpdateButton"
            onClick={handleUpdateBilling}
            isLoading={isLoading}
            disabled={isLoading}
            variant="primary"
            color="purple"
            style={sty.primaryButton}
          >
            {isLoading ? 'Opening Billing Portal...' : 'Update Payment Method'}
          </ButtonSymphony>
        </div>
      </div>
    </ModalSymphony>
  );
};

export default OrganizationPaymentPastDueModal;
